import { useCallback } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  completeTodo as completeTodoRequest,
  declineOffer as declineOfferRequest,
  fetchFormQuestions as fetchFormQuestionsRequest,
  signOffer as signOfferRequest,
  submitForm as submitFormRequest,
  uploadFiles as uploadFilesRequest
} from './api';
import { onboardQueryKeys } from '../onboardQueryKeys';

import type { IAnswer } from '@host/components/NewUI/ApprovalForms/types';
import type { IApplicantQuestion } from '@host/components/NewUI/Components/Utilities/QuestionFieldGenerator';

import type { IPortalState } from './types';

interface UsePortalActionsResult {
  uploadFiles: (itemId: string, files: File[]) => Promise<void>;
  completeTodo: (itemId: string) => Promise<IPortalState | undefined>;
  submitForm: (
    itemId: string,
    payload: { form_id: number; answers_attributes: Record<number, unknown> }
  ) => Promise<void>;
  signOffer: (signatureData: string) => Promise<IPortalState>;
  declineOffer: (notes: string) => Promise<void>;
  fetchFormQuestions: (
    itemId: string
  ) => Promise<{ form_id: number; fields: IApplicantQuestion[]; answers: IAnswer[] }>;
  isFetchingFormQuestions: boolean;
}

/**
 * Write-side wiring for the candidate portal. Each mutation updates the
 * shared `portalState` query cache on success so PortalHome/Portal.tsx always
 * render the latest server state after a user action - `uploadFiles`,
 * `completeTodo` and `submitForm` invalidate and let the next read refetch
 * (mirroring the previous `reload()` helper); `signOffer`/`declineOffer`
 * write the response straight into the cache since the server already
 * returns (or implies) the updated state, avoiding an extra round trip.
 */
export function usePortalActions(token: string): UsePortalActionsResult {
  const queryClient = useQueryClient();
  const stateKey = onboardQueryKeys.portalState(token);

  const uploadFilesMutation = useMutation({
    mutationFn: async ({ itemId, files }: { itemId: string; files: File[] }) =>
      uploadFilesRequest(token, itemId, files),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: stateKey })
  });

  const completeTodoMutation = useMutation({
    mutationFn: async (itemId: string) => completeTodoRequest(token, itemId),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: stateKey })
  });

  const submitFormMutation = useMutation({
    mutationFn: async ({
      itemId,
      payload
    }: {
      itemId: string;
      payload: { form_id: number; answers_attributes: Record<number, unknown> };
    }) => submitFormRequest(token, itemId, payload),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: stateKey })
  });

  const signOfferMutation = useMutation({
    mutationFn: async (signatureData: string) => signOfferRequest(token, signatureData),
    onSuccess: (next) => {
      queryClient.setQueryData(stateKey, next);
      // Drop the unsigned contract outright (not just invalidate) so reopening
      // the preview loads the signed PDF instead of flashing the cached one.
      queryClient.removeQueries({ queryKey: onboardQueryKeys.portalContract(token) });
    }
  });

  const declineOfferMutation = useMutation({
    mutationFn: async (notes: string) => declineOfferRequest(token, notes),
    onSuccess: () =>
      queryClient.setQueryData<IPortalState | undefined>(stateKey, (current) =>
        current ? { ...current, declined: true } : current
      )
  });

  const fetchFormQuestionsMutation = useMutation({
    mutationFn: async (itemId: string) => fetchFormQuestionsRequest(token, itemId)
  });

  const uploadFiles = useCallback(
    async (itemId: string, files: File[]) => {
      await uploadFilesMutation.mutateAsync({ itemId, files });
    },
    [uploadFilesMutation]
  );

  // Resolves with the refetched portal state (the mutation's onSuccess awaits
  // the invalidation refetch) so callers can navigate based on the fresh
  // `all_complete` flag, matching the previous manual `reload()` behaviour.
  const completeTodo = useCallback(
    async (itemId: string) => {
      await completeTodoMutation.mutateAsync(itemId);
      return queryClient.getQueryData<IPortalState>(stateKey);
    },
    [completeTodoMutation, queryClient, stateKey]
  );

  const submitForm = useCallback(
    async (
      itemId: string,
      payload: { form_id: number; answers_attributes: Record<number, unknown> }
    ) => {
      await submitFormMutation.mutateAsync({ itemId, payload });
    },
    [submitFormMutation]
  );

  const signOffer = useCallback(
    async (signatureData: string) => signOfferMutation.mutateAsync(signatureData),
    [signOfferMutation]
  );

  const declineOffer = useCallback(
    async (notes: string) => {
      await declineOfferMutation.mutateAsync(notes);
    },
    [declineOfferMutation]
  );

  const fetchFormQuestions = useCallback(
    async (itemId: string) => fetchFormQuestionsMutation.mutateAsync(itemId),
    [fetchFormQuestionsMutation]
  );

  return {
    uploadFiles,
    completeTodo,
    submitForm,
    signOffer,
    declineOffer,
    fetchFormQuestions,
    isFetchingFormQuestions: fetchFormQuestionsMutation.isLoading
  };
}
